import { z } from 'zod'

/**
 * Output transform for timestamp fields that the DB hands back as `Date`
 * but the wire shape declares as ISO strings. Accepts either input
 * (`string | Date`) and normalises to `string` on parse.
 *
 * Use this everywhere a contract schema describes a `createTime` /
 * `updateTime` / `airStartTime` / etc. field that downstream consumers
 * read as a string.
 */
export const DateToIsoString = z
  .union([z.string(), z.date()])
  .transform(v => (typeof v === 'string' ? v : v.toISOString()))

/**
 * Input transform for a request body that carries a timestamp as an ISO string
 * on the wire and hands a `Date` to the service behind it. Accepts `string`
 * only, so the schema's input type stays exactly the wire type.
 *
 * That matters under the contract `request` pattern (`defineRouter` in
 * `apps/select-api/src/routes/context.ts`): a route's `body` schema must parse
 * to exactly the shape it accepts, so the body declares `z.iso.datetime()` and
 * the request schema — whose input is that same wire shape — swaps in this
 * transform to produce the `Date`.
 */
export const IsoStringToDate = z.iso.datetime().transform(v => new Date(v))

/**
 * Nullable variant for soft-deleted rows where `deleteTime` etc. can be
 * `null` until the row is actually deleted.
 */
export const NullableDateToIsoString = z
  .union([z.string(), z.date(), z.null()])
  .transform(v => (v == null ? null : typeof v === 'string' ? v : v.toISOString()))
